<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/api/index';
import { trackAdditionalBusinessContextSuggestionEvent } from '@/api/tracking';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import Header from '@/components/layouts/header.vue';
import OrganizationTagLabels from '@/components/others/organization-tags/organization-tag-labels.vue';
import PersonalSurveyProfileForm from '@/components/settings/personal-survey-profile-form.vue';
import type {
  AdditionalBusinessContextSuggestionRefreshFrom,
  PersonalSurveyProfileInput,
} from '@/types';
import {
  toPersonalSurveyProfileInput,
  toProfileTrackingCategory,
} from '@/utils/personalSurveyProfile';
import {
  usePersonalSurveyProfile,
  useTeamInfo,
  useUserInfo,
  useUserSettingsProfile,
} from '@/utils/swr';
import { featureFlags } from '@/featureFlags';

const route = useRoute();
const { createSnackbar } = useSnackbar();

const { data: userInfo } = useUserInfo();
const { data: userSettings } = useUserSettingsProfile();
const { data: teamInfo } = useTeamInfo();
const enableProfile = computed(() => teamInfo.value?.enable_profile ?? false);
const { data: personalSurveyProfile } = usePersonalSurveyProfile(enableProfile);

const profile = ref<PersonalSurveyProfileInput>({
  department: '',
  market_area: '',
  technology_area: '',
  technical_challenge: '',
});
const savedProfile = ref<PersonalSurveyProfileInput>({ ...profile.value });
const formValid = ref(true);
const suggestionApplyCount = ref(0);

const PAGE_NAME = 'profile';

const enableSubmit = computed(() => formValid.value);

watch(
  personalSurveyProfile,
  () => {
    if (!personalSurveyProfile.value) return;
    profile.value = toPersonalSurveyProfileInput(personalSurveyProfile.value);
    savedProfile.value = toPersonalSurveyProfileInput(
      personalSurveyProfile.value,
    );
  },
  { immediate: true },
);

onMounted(() => {
  const from = route.query.from;

  // メールからの流入を計測
  if (from === 'mail') {
    const trackingData = {
      from,
      event_source: {
        name: route.query.event_source_name as string,
      },
    };

    api.trackEvent(
      'profile_edit_click',
      {
        pageName: PAGE_NAME,
        pageUrl: route.fullPath,
      },
      undefined,
      undefined,
      trackingData,
    );
  }
});

const onSuggestionApply = () => {
  suggestionApplyCount.value += 1;
  void trackAdditionalBusinessContextSuggestionEvent(
    'additional_business_context_suggestion_apply',
    { page: { name: PAGE_NAME } },
  );
};
const onSuggestionRefresh = (
  from: AdditionalBusinessContextSuggestionRefreshFrom,
) => {
  void trackAdditionalBusinessContextSuggestionEvent(
    'additional_business_context_suggestion_refresh',
    { page: { name: PAGE_NAME }, context: { from } },
  );
};

const submit = async () => {
  if (!userSettings.value) return;
  const sentSuggestionApplyCount = suggestionApplyCount.value;
  suggestionApplyCount.value = 0;
  try {
    const profileInput = { ...profile.value };
    if (enableProfile.value) {
      await api.updatePersonalSurveyProfile(profileInput);
    }

    await api.updateUserSettingsProfile(
      userSettings.value,
      {
        pageName: PAGE_NAME,
        pageUrl: route.fullPath,
      },
      enableProfile.value
        ? toProfileTrackingCategory(
            savedProfile.value,
            profileInput,
            sentSuggestionApplyCount,
          )
        : undefined,
    );
    if (enableProfile.value) savedProfile.value = profileInput;

    createSnackbar({
      message: '設定を更新しました',
      type: 'success',
    });
  } catch (e) {
    suggestionApplyCount.value += sentSuggestionApplyCount;
    createSnackbar({
      message: '設定を更新できませんでした',
      type: 'error',
    });
    throw e;
  }
};
</script>
<template>
  <div class="user-settings" v-if="userInfo && userSettings">
    <Header title="プロフィール" header-width="min(912px, 100%)" />
    <div class="settings-body">
      <div class="settings-card">
        <div class="c-formBlock">
          <div class="c-formBlock__label">メールアドレス</div>
          <span class="c-formBlock__text">{{ userInfo.email }}</span>
        </div>
        <div class="c-formBlock">
          <div class="c-formBlock__label">名前</div>
          <span class="c-formBlock__text">{{ userInfo.user_name }}</span>
        </div>
        <fieldset
          v-if="
            enableProfile &&
            featureFlags.ANDEV_10700_INSIGHT_ADDITIONAL_BUSINESS_CONTEXT
          "
          class="business-info"
        >
          <legend class="c-formBlock__label">業務情報</legend>
          <div class="business-info-frame">
            <PersonalSurveyProfileForm
              v-model="profile"
              v-model:valid="formValid"
              with-additional-business-context-suggestion
              @suggestion-apply="onSuggestionApply"
              @suggestion-refresh="onSuggestionRefresh"
            />
          </div>
        </fieldset>
        <PersonalSurveyProfileForm
          v-else-if="enableProfile"
          v-model="profile"
          v-model:valid="formValid"
        />

        <div class="c-formBlock__label">所属中の組織タグ</div>
        <p class="c-formBlock__text description">
          管理者のみ「組織タグ管理」から追加・編集できます
        </p>
        <OrganizationTagLabels
          class="organization-tag-list"
          :labels="userInfo.organization_tags"
          :deletable="false"
        />
      </div>
    </div>
    <div class="settings-footer">
      <div class="settings-footer-inner">
        <button
          class="c-btn c-btn--small c-btn--auto c-btn--AnewsPrimary"
          :class="{ disabled: !enableSubmit }"
          @click="submit"
          :disabled="!enableSubmit"
        >
          保存
        </button>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
// 内容が短くても画面の最下部に置く
.settings-footer {
  position: sticky;
  bottom: 0;
  margin-top: auto;
  padding: 16px 24px;
  background: $color-white;
  box-shadow: 0px 1px 5px rgba($color-gray1000, 0.25);

  .settings-footer-inner {
    max-width: 912px;
    margin: 0 auto;
  }

  .c-btn {
    width: 60px;
    margin: 0;
    &.disabled {
      border: none;
    }
  }
}

.user-settings {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;

  .settings-body {
    box-sizing: border-box;
    width: 100%;
    max-width: calc(912px + 48px);
    margin: 0 auto;
    padding: 24px;
  }

  .settings-card {
    box-sizing: border-box;
    padding: 16px;
    background: #fff;
    border: 1px solid $color-border;
    border-radius: $border-radius;

    .c-formBlock {
      margin-bottom: 12px;
    }

    .business-info {
      margin: 0 0 12px;
      padding: 0;
      border: none;

      .c-formBlock__label {
        padding: 0;
        margin-bottom: 8px;
      }
    }

    .business-info-frame {
      padding: 16px;
      border: 1px solid $color-border;
      border-radius: $border-radius;

      :deep(.c-formBlock:last-child) {
        margin-bottom: 0;
      }
    }

    textarea,
    input {
      outline: none;
    }

    .organization-tag-list {
      margin-top: 4px;
      min-height: 50px;
      align-items: start;
    }

    .company-name-input {
      margin-bottom: 14.5px; // 他のinput下部に設置されているTextCounterのheight分の余白を追加する
    }

    .description {
      color: $color-gray600;
      font-size: 12px;
    }
  }
}
</style>
